import { useState, useEffect, useCallback } from "react";
import { AUTH_STATE_CHANGED_EVENT, authStorage } from "../lib/authStorage";
import { userService } from "../services/services";
import type { UserDetails } from "../types/domain";

export function useCurrentUser(): {
  userDetails: UserDetails | null;
  loading: boolean;
  refetch: () => Promise<UserDetails | null>;
} {
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchUserDetails = useCallback(async (forceFetch = false) => {
    const userId = authStorage.getUserId();
    if (!userId) {
      setUserDetails(null);
      return null;
    }

    if (!forceFetch) {
      const cached = authStorage.getUserDetails<UserDetails>();
      if (cached) {
        setUserDetails(cached);
        return cached;
      }
    }

    setLoading(true);
    try {
      const user = await userService.getUserDetails(userId);
      authStorage.setUserDetails(user);
      setUserDetails(user);
      return user;
    } catch {
      const cached = authStorage.getUserDetails<UserDetails>();
      setUserDetails(cached);
      return cached;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserDetails();

    const syncState = () => {
      const cached = authStorage.getUserDetails<UserDetails>();
      setUserDetails(cached);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncState);
      window.addEventListener(AUTH_STATE_CHANGED_EVENT, syncState);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", syncState);
        window.removeEventListener(AUTH_STATE_CHANGED_EVENT, syncState);
      }
    };
  }, [fetchUserDetails]);

  return {
    userDetails,
    loading,
    refetch: () => fetchUserDetails(true),
  };
}
